/**
 * The facility's records as patient bundles, for worklists and indicators. Decrypted in memory
 * only; reloaded when the store changes (save or sync).
 */
import { type AnyResource, type PatientBundle, bundlesOf } from '@assist/core';
import { useEffect, useState } from 'preact/hooks';
import { app } from './controller.js';
import { useApp } from './hooks.js';

const BASE_TYPES = ['Patient', 'EpisodeOfCare', 'Encounter', 'Task', 'ServiceRequest'] as const;

/** All patients with their records. Observations and medicines only when asked for (reports). */
export async function loadBundles(withObservations = false): Promise<PatientBundle[]> {
  const s = app.session;
  if (!s) return [];
  const types: string[] = [
    ...BASE_TYPES,
    ...(withObservations ? ['Observation', 'MedicationRequest'] : []),
  ];
  const lists = await Promise.all(types.map((type) => s.list<AnyResource>(type)));
  return bundlesOf(lists.flat());
}

// ------------------------------------------------------------------ shared, cached for the screen
let cacheKey = '';
let cached: Promise<PatientBundle[]> | null = null;

/**
 * The facility's bundles (without observations), loaded once per store change and shared by every
 * part of the screen that counts (side navigation, home tiles, worklists).
 */
export function useBundles(): PatientBundle[] | null {
  const a = useApp();
  const key = JSON.stringify([a.session?.user.id ?? '', a.status]);
  const [state, setState] = useState<{ key: string; bundles: PatientBundle[] } | null>(null);
  useEffect(() => {
    if (!a.session) return;
    if (key !== cacheKey || !cached) {
      cacheKey = key;
      cached = loadBundles();
    }
    let live = true;
    void cached.then((b) => live && setState({ key, bundles: b }));
    return () => {
      live = false;
    };
  }, [key, a.session]);
  return state?.bundles ?? null;
}
